import React, { useState, useRef, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";


// Components
import RechartFlow from "pages/dashboard/rechart/RechartFlow";
import EmployeeTask from "pages/dashboard/EmployeeTasks/EmployeeTask";
import HighAndLowMovingProducts from "./HighAndLowMoving/HighAndLowMovingProduct";
import DataTable from "components/DataTable";
import GenericModal from "components/Modal";
import FilterComponent from "components/filter/FilterComponent";
import UsersVisitList from "./visit/UsersVisitList";
import { showToast } from "components/Toaster";

// Custom Dashboard Components
import DashboardHeader from "components/dashboard/DashboardHeader";
import DashboardControls from "components/dashboard/DashboardControls";
import DashboardSection from "components/dashboard/DashboardSection";


// Services & Utilities
import httpClient from "services/network/httpClient";
import { faEye } from "@fortawesome/free-solid-svg-icons";
import TotalSummary from "pages/summary/TotalSummary";
import DemoReportFilter, { FilterFormValues } from "pages/summary/demo/DemoReportFilter";
import FilterBadge, { FilterChip } from "components/filter/FilterBadge";
import { msToDateInput } from "../summary/DemoReport"

// Utility to convert ms/epoch to YYYY-MM-DD string for input fields and chips
// function msToDateInput(ms?: string | number): string {
//   if (!ms) return "";
//   const msNum = typeof ms === "string" ? parseInt(ms, 10) : ms;
//   const d = new Date(msNum < 1e12 ? msNum * 1000 : msNum);
//   return d.toISOString().slice(0, 10);
// }

// Types
export interface EmployeeTaskProps {
  userId: string;
  employeeName: string;
  city: string;
  totalVisits: number;
  completedDemo: number;
  pending: number
}

export interface DashboardStats {
  totalDemos: number;
  completedDemos: number;
  pendingDemos: number;
  totalEmployees: number;
}

interface VisitActionCellProps {
  row: any;
  onView: (row: any) => void;
}

const VisitActionCell: React.FC<VisitActionCellProps> = ({ row, onView }) => (
  <div className="flex space-x-2">
    <button
      onClick={() => onView(row)}
      className="p-2 
                        bg-blue-50 dark:bg-blue-900/40 
                        text-blue-600 dark:text-blue-300 
                        hover:bg-blue-100 dark:hover:bg-blue-900/60 
                        rounded-lg transition-colors"
      title="View Details"
    >
      <FontAwesomeIcon icon={faEye} />
    </button>
  </div>
);

const Dashboard = () => {
  // State Management
  const [isLoading, setIsLoading] = useState(false);
  const [isFilter, setIsFilter] = useState(false);
  const [isDemoList, setIsDemoList] = useState(true);
  const [isVisitList, setIsVisitList] = useState(false);
  const [visitView, setVisitView] = useState(false);
  const [isFilterPayload, setIsFilterPayload] = useState<string>("");
  const [openReportModal, setOpenReportModal] = useState(false);
  // Data State
  const [employeeTask, setEmployeeTask] = useState<any[]>([]);
  const [monthlyTask, setMonthlyTask] = useState<any[]>([]);
  const [demoCount, setDemoCount] = useState<any>(null);
  const [highMoving, setHighMoving] = useState<any[]>([]);
  const [lowMoving, setLowMoving] = useState<any[]>([]);
  const [productVisit, setProductVisit] = useState<any[]>([]);
  const [visitViewData, setVisitViewData] = useState<EmployeeTaskProps>({} as EmployeeTaskProps);
  const [filterData, setFilterData] = useState<any>();
  const [activeFilterParams, setActiveFilterParams] = useState<Record<string, string>>({});
  const activeFilterParamsRef = useRef<Record<string, string>>({});
  const [activeFilterFormValues, setActiveFilterFormValues] = useState<Partial<FilterFormValues>>({});
  const [filterChips, setFilterChips] = useState<FilterChip[]>([]);
  const [activeFilterLabels, setActiveFilterLabels] = useState<{
    fromDate?: string;
    toDate?: string;
    employeeLabel?: string;
    managerLabel?: string;
  }>({});
  const [dashboardStats, setDashboardStats] = useState<DashboardStats>({
    totalDemos: 0,
    completedDemos: 0,
    pendingDemos: 0,
    totalEmployees: 0
  });
  console.log("Dashboard Stats:", dashboardStats);
  const [searchTerm, setSearchTerm] = useState("");
  const filterCount = isFilterPayload
    ? new URLSearchParams(isFilterPayload).toString().split("&").length
    : 0;
  // Filtered data based on search text
  const filteredEmployeeTask = employeeTask.filter(item =>
    Object.values(item).some(v =>
      v?.toString().toLowerCase().includes(searchTerm.toLowerCase())
    )
  );
  //search field fix
  const filteredProductVisit = productVisit.filter((item) =>
    item.employeeName.toLowerCase().includes(searchTerm.toLowerCase())
  );


  // Refs
  const printRef = useRef<HTMLDivElement>(null);

  /**
   * Fetches dashboard data for demo analysis
   */
  const getDemoList = (overrideParams?: Record<string, string>) => {
    setIsLoading(true);
    const demoApiUrl = overrideParams
      ? `/product-demo/dashboard?${new URLSearchParams(overrideParams).toString()}`
      : "/product-demo/dashboard";
    httpClient
      .get(demoApiUrl)
      .then((res: any) => {
        setEmployeeTask(res.value.employeeDemoCounts);
        setMonthlyTask(res.value.demoReport.monthlyDemos);
        setDemoCount(res.value.demoReport.totalCounts);
        setHighMoving(res.value.topHighAndLowMovingProducts.highMoving);
        setLowMoving(res.value.topHighAndLowMovingProducts.lowMoving);

        // Calculate dashboard stats
        setDashboardStats({
          totalDemos: (res.value.demoReport.totalCounts?.totalCompletedDemos || 0) +
            (res.value.demoReport.totalCounts?.totalPendingDemos || 0),
          completedDemos: res.value.demoReport.totalCounts?.totalCompletedDemos || 0,
          pendingDemos: res.value.demoReport.totalCounts?.totalPendingDemos || 0,
          totalEmployees: res.value.employeeDemoCounts?.length || 0
        });
      })
      .catch((error: any) => {
        console.error("Error fetching product demo count: ", error);
        showToast("Failed to load dashboard data", "error");
      })
      .finally(() => {
        setIsLoading(false);
      });
  }

  /**
   * Fetches visit analysis data
   */
  //Updated getVisitList to accept optional parameters- filter issue fix
  const getVisitList = (params?: string) => {
    setIsLoading(true);
    // Use params directly if provided, otherwise fallback to state
    const apiUrl = params ? `/v1/visit/summary/visits/team?${params}` : "/v1/visit/summary/visits/team";

    httpClient
      .get(apiUrl)
      .then((res: any) => {
        setProductVisit(res.value);
      })
      .catch((error: any) => {
        console.error("Error fetching product visit count: ", error);
        showToast("Failed to load visit data", "error");
      })
      .finally(() => {
        setIsLoading(false);
      });
  }

  /**
   * Fetches data based on current view
   */
  const refreshData = () => {
    if (isDemoList) {
      getDemoList();
    } else if (isVisitList) {
      getVisitList();
    }
  };

  // Initial data fetch on component mount or view change
  useEffect(() => {
    if (isDemoList || (!isDemoList && !isVisitList)) {
      getDemoList();
    } else if (isVisitList) {
      getVisitList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDemoList, isVisitList]);


  /**
   * Handles printing the dashboard
   */


  /**
   * Handles opening the visit details view
   */
  const handleView = (row: any) => {
    setVisitView(true);
    setVisitViewData(row);
    setFilterData({} as any);
  };

  /**
   * Toggles the demo dashboard view
   */
  const handleToggleDemoView = () => {
    setIsDemoList(true);
    setIsVisitList(false);
    setSearchTerm("");
  };

  /**
   * Toggles the visit analysis view
   */
  const handleToggleVisitView = () => {
    setIsVisitList(true);
    setIsDemoList(false);
    setSearchTerm("");
  };
  const handleResetFilter = () => {
    setSearchTerm("");
    setIsFilterPayload("");
    setFilterData(undefined);
    setActiveFilterLabels({});
    activeFilterParamsRef.current = {};
    setActiveFilterParams({});
    setActiveFilterFormValues({});
    setFilterChips([]);
    setIsFilter(false);

    if (isDemoList) {
      getDemoList();
    } else if (isVisitList) {
      getVisitList();
    }
  };

  /**
   * Handles filter form submission
   */
  //filter render fixes
  const handleFilterSubmit = (data: any) => {
    // const url = new URLSearchParams();
    console.log("Filter data received for submission:", data);

    // Process dates
    // if (data.fromDate) {
    //   const fromDateConcat = data.fromDate + "T00:00:00Z";
    //   url.append("fromDate", fromDateConcat);
    // }
    // if (data.toDate) {
    //   const toDateConcat = data.toDate + "T23:59:59Z";
    //   url.append("toDate", toDateConcat);
    // }

    // Process other fields
    // if (data.month) url.append("month", data.month);
    // if (data.year) url.append("year", data.year);
    // if (data.status) url.append("status", data.status);
    // if (data.employeeId) url.append("employeeId", data.employeeId);

    const params: Record<string, string> = {};
    if (data.fromDate) params.fromDate = `${data.fromDate}`;
    if (data.toDate) params.toDate = `${data.toDate}`;
    if (data.manager && data.manager !== "All") params.managerId = data.manager;
    if (data.employee && data.employee !== "All") {
      if (isDemoList) params.employeeId = data.employee;
      else params.empIds = data.employee;
    }
    if ((data as any).product && (data as any).product !== "All") params.productId = (data as any).product;
    if ((data as any).crop && (data as any).crop !== "All") params.cropId = (data as any).crop;
    if ((data as any).status === 0 || (data as any).status === 1 && (data as any).status !== "All") params.status = (data as any).status;
    if ((data as any).distributor && (data as any).distributor !== "All") params.distributor = (data as any).distributor;



    setActiveFilterLabels({
      fromDate: msToDateInput(params.fromDate),
      toDate: msToDateInput(params.toDate),
      employeeLabel: (data as any).__employeeLabel,
      managerLabel: (data as any).__managerLabel,
    });
    activeFilterParamsRef.current = params;
    setActiveFilterParams(params);
    // Store filter parameters in state
    const filterParams = new URLSearchParams(params).toString();
    setIsFilterPayload(filterParams);
    // Always store as string for popup
    setActiveFilterFormValues({
      ...data,
      fromDate: msToDateInput(params.fromDate),
      toDate: msToDateInput(params.toDate),
    });

    // Build filter chips
    const chips: FilterChip[] = [];
    if (params.fromDate || params.toDate) {
      chips.push({
        id: "dateRange",
        label: `Date: ${msToDateInput(params.fromDate)} – ${msToDateInput(params.toDate)}`,
        onRemove: () => removeChip("dateRange", ["fromDate", "toDate"], ["fromDate", "toDate"]),
      });
    }
    if (params.managerId) {
      chips.push({
        id: "manager",
        label: `Manager: ${(data as any).__managerLabel ?? params.managerId}`,
        onRemove: () => removeChip("manager", ["managerId"], ["manager"]),
      });
    }
    if (params.employeeId || params.empIds) {
      const empParamKey = params.employeeId ? "employeeId" : "empIds";
      const empValue = params.employeeId ?? params.empIds;
      chips.push({
        id: "employee",
        label: `Employee: ${(data as any).__employeeLabel ?? empValue}`,
        onRemove: () => removeChip("employee", [empParamKey], ["employee"]),
      });
    }
    if (params.productId) {
      chips.push({
        id: "product",
        label: `Product: ${(data as any).__productLabel ?? params.productId}`,
        onRemove: () => removeChip("product", ["productId"], ["product"]),
      });
    }
    if (params.cropId) {
      chips.push({
        id: "crop",
        label: `Crop: ${(data as any).__cropLabel ?? params.cropId}`,
        onRemove: () => removeChip("crop", ["cropId"], ["crop"]),
      });
    }
    if (params.status !== undefined) {
      chips.push({
        id: "status",
        label: `Status: ${(data as any).__statusLabel ?? params.status}`,
        onRemove: () => removeChip("status", ["status"], ["status"]),
      });
    }
    setFilterChips(chips);

    if (isDemoList) {
      // ✅ Demo view filter logic stays the same
      setIsLoading(true);
      // const api = isVisitList
      httpClient
        .get(`product-demo/dashboard?${filterParams}`)
        .then((res: any) => {
          setEmployeeTask(res.value.employeeDemoCounts);
          setMonthlyTask(res.value.demoReport.monthlyDemos);
          setDemoCount(res.value.demoReport.totalCounts);
          setHighMoving(res.value.topHighAndLowMovingProducts.highMoving);
          setLowMoving(res.value.topHighAndLowMovingProducts.lowMoving);

          setDashboardStats({
            totalDemos: (res.value.demoReport.totalCounts?.totalCompletedDemos || 0) +
              (res.value.demoReport.totalCounts?.totalPendingDemos || 0),
            completedDemos: res.value.demoReport.totalCounts?.totalCompletedDemos || 0,
            pendingDemos: res.value.demoReport.totalCounts?.totalPendingDemos || 0,
            totalEmployees: res.value.employeeDemoCounts?.length || 0
          });
        })
        .catch((error: any) => {
          console.error("Error fetching filtered sessions:", error);
        })
        .finally(() => {
          setIsLoading(false);
          setIsFilter(false);
        });
    } else if (isVisitList) {
      // ✅ Pass filterParams directly to ensure API uses latest filters
      setIsLoading(true);
      getVisitList(filterParams);  // <-- Changed this line
      setIsFilter(false);
    }
  };

  const onSubmit = () => {
    console.log('hello');
  }
  const handleClearFilter = () => {
    activeFilterParamsRef.current = {};
    setActiveFilterParams({});
    setActiveFilterFormValues({});
    setFilterChips([]);
    setActiveFilterLabels({});
    setIsFilterPayload("");
    getDemoList();
  }

  const removeChip = (chipId: string, paramKeys: string[], formKeys: string[]) => {
    // Read from ref — always current even in stale closures
    const newParams = { ...activeFilterParamsRef.current };
    if (chipId === "dateRange") {
      delete newParams.fromDate;
      delete newParams.toDate;
      setActiveFilterLabels(prev => ({ ...prev, fromDate: undefined, toDate: undefined }));
    } else {
      paramKeys.forEach(k => delete newParams[k]);
      if (chipId === "employee") setActiveFilterLabels(prev => ({ ...prev, employeeLabel: undefined }));
      if (chipId === "manager") setActiveFilterLabels(prev => ({ ...prev, managerLabel: undefined }));
    }
    setFilterChips(prev => prev.filter(c => c.id !== chipId));
    activeFilterParamsRef.current = newParams;
    setActiveFilterParams(newParams);
    setActiveFilterFormValues(prev => {
      const next = { ...prev } as any;
      // Always store as string for popup
      next.fromDate = msToDateInput(newParams.fromDate);
      next.toDate = msToDateInput(newParams.toDate);
      formKeys.forEach(k => { if (k !== "fromDate" && k !== "toDate") delete next[k]; });
      return next;
    });
    const paramStr = new URLSearchParams(newParams).toString();
    setIsFilterPayload(paramStr);
    if (isDemoList) {
      getDemoList(Object.keys(newParams).length > 0 ? newParams : undefined);
    } else if (isVisitList) {
      getVisitList(paramStr || undefined);
    }
  };

  const performanceTitle = useMemo(() => {
    const { fromDate, toDate, employeeLabel, managerLabel } = activeFilterLabels;
    if (!fromDate && !toDate && !employeeLabel && !managerLabel) {
      return "Monthly Performance Overview";
    }
    const namePart = employeeLabel ?? managerLabel;
    const datePart = fromDate && toDate ? `${fromDate} – ${toDate}` : fromDate ?? toDate;
    if (namePart && datePart) return `Performance Overview · ${namePart} · ${datePart}`;
    if (namePart) return `Performance Overview · ${namePart}`;
    if (datePart) return `Performance Overview · ${datePart}`;
    return "Monthly Performance Overview";
  }, [activeFilterLabels]);

  return (
    <div className="flex flex-col bg-white dark:bg-gray-900 w-full h-full overflow-x-hidden">
      {/* Dashboard Header */}
      <DashboardHeader
        title="Team Performance Dashboard"
        subtitle="Monitor team activity, product performance, and customer engagement"
        onRefresh={refreshData}
        onPrint={() => setOpenReportModal(true)}

      />

      {/* Stats Cards */}


      {/* Dashboard Controls */}
      <DashboardControls
        isDemoView={isDemoList}
        isVisitView={isVisitList}
        onToggleDemoView={handleToggleDemoView}
        onToggleVisitView={handleToggleVisitView}
        onOpenFilter={() => setIsFilter(true)}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        onResetFilter={handleResetFilter}
        isFilterApplied={!!isFilterPayload}
        filterCount={filterCount}
      />

      <FilterBadge count={Object.keys(activeFilterParams).length} onClear={handleClearFilter} chips={filterChips} />

      {/* Main Dashboard Content */}
      <div className="pb-6">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : isDemoList ? (
          <div className="space-y-6">
            {/* Chart Section */}
            <DashboardSection
              title={performanceTitle}
              fullWidth
            >
              <div className="h-[400px]">
                <RechartFlow data={monthlyTask} totalCount={demoCount} />
              </div>
            </DashboardSection>

            {/* Data Section */}
            <div ref={printRef} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* High & Low Moving Products */}
              <DashboardSection
                title="Product Performance"
                delay={0.1}
              >
                <HighAndLowMovingProducts highMoving={highMoving} lowMoving={lowMoving} />
              </DashboardSection>

              {/* Employee Tasks */}
              <DashboardSection
                title="Employee Performance"
                delay={0.2}
                fullWidth
              >
                {isFilter && filterData && filterData !== null ? (
                  <EmployeeTask employeeTask={filterData} isFiltered={isFilterPayload} />
                ) : (
                  <EmployeeTask
                    employeeTask={filteredEmployeeTask}
                    isFiltered={isFilterPayload}
                  />
                )}
              </DashboardSection>
            </div>
          </div>
        ) : (
          <DashboardSection title="Visit Analysis">
            {(() => {
              const visitTableData = isVisitList ? filteredProductVisit : productVisit;
              return (
                <DataTable
                  data={visitTableData}
                  columns={[

                    {
                      key: "managerName", label: "Manager Name",

                      render: (_: any, row: any) => (
                        <div className="flex flex-row item-center">
                          <div>
                            <div className="font-medium text-sm text-gray-800 dark:text-gray-200">{row.managerName || 'Direct'}</div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {(row.managerCode !== "N/A") && row.managerCode}
                            </div>
                          </div>
                        </div>
                        // <span>{row.managerCode && row.managerCode !== "N/A" ? `${row.managerCode}/${row.managerName}` : (row.managerName ?? "—")}</span>
                      ),
                    },
                    {
                      key: "employeeName", label: "Employee Name",
                      render: (_: any, row: any) => (
                        <div className="flex flex-row item-center">
                          <div>
                            <div className="font-medium text-gray-800 dark:text-gray-200">{row.employeeName || 'Direct'}</div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {row.employeeCode && row.employeeCode}
                            </div>
                          </div>
                        </div>
                        // <span>{row.employeeCode ? `${row.employeeCode}/${row.employeeName}` : row.employeeName}</span>
                      ),
                    },
                    { key: "totalCount", label: "Total Visits" },
                    { key: "completedCount", label: "Completed" },
                    { key: "pendingCount", label: "Follow Up" },

                    {
                      key: "actions",
                      label: "Actions",
                      excludeExport: true,
                      actions: (row: any) => <VisitActionCell row={row} onView={handleView} />,
                    },
                  ]}
                  isAddBtnTrue={false}
                  isExport={false}
                  isSearchBtn={false}
                />
              );
            })()}

          </DashboardSection>
        )}
      </div>


      {
        isFilter && (
          <div
            style={{
              position: 'absolute',
              zIndex: 50,
              top: 240,
              right: 50,
              marginTop: '8px',
              width: '400px',
              maxWidth: 'calc(100vw - 2rem)'
            }}
          >
            <DemoReportFilter onClose={() => setIsFilter(false)} title="Summary Filter" onSubmit={handleFilterSubmit} onReset={handleClearFilter} showManager={false} showStatus={true} initialValues={activeFilterFormValues} />
          </div>
        )
      }

      {/* Visit Details Modal */}
      {visitView && (
        <GenericModal
          isOpen={visitView}
          onClose={() => setVisitView(false)}
          header={`Visit Details for ${visitViewData?.employeeName}`}
          size="xl"
          customHeight="80vh"
        >
          <UsersVisitList
            infoData={visitViewData}
            filterParams={isVisitList && isFilterPayload ? isFilterPayload : ""}
          />
        </GenericModal>
      )}
      <AnimatePresence>
        {openReportModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 30 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-lg "
            >
              <TotalSummary

              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};



export default Dashboard;
